import { useSOWDataStore } from './useSOWDataStore';
import { queryParamsService } from '../services/queryParams';

// Tickets stored before the parts usage task existed learn whether they need it only once the
// work order's parts are known, which can take a fetch. Kept out of the persisted tasks store so
// a reload never restores a check that is no longer running.
const checksInFlight = new Map();

// Tickets whose last check could not get the work order's data: whether they need the task is unknown.
const unverified = new Set();
export const isPartsUsageUnverified = (sowKey) => unverified.has(sowKey);
export const markPartsUsageVerified = (sowKey, isVerified) => {
  if (isVerified) unverified.delete(sowKey);
  else unverified.add(sowKey);
};

export const isCheckingPartsUsage = (sowKey) => checksInFlight.has(sowKey);

// One check per ticket at a time: a second caller shares the running one.
export const runPartsUsageCheck = (sowKey, check) => {
  const running = checksInFlight.get(sowKey);
  if (running) return running;
  const promise = Promise.resolve().then(check).finally(() => checksInFlight.delete(sowKey));
  checksInFlight.set(sowKey, promise);
  return promise;
};

const matchingSOWData = () => {
  const { sowData } = useSOWDataStore.getState();
  return sowData && sowData.workorderKey === queryParamsService.getIssueKey() ? sowData : null;
};

// Bounded like the data store's own loading guard: a request that never settles must not keep
// the check running for good.
const SOW_LOAD_WAIT_LIMIT_MS = 5000;

// Resolves true once the load has finished, false if it was still running when the wait gave up.
// The two must stay apart: only a finished refresh can vouch for the data it left behind.
const waitForSOWLoadToEnd = () => new Promise((resolve) => {
  let timer = null;
  let isDone = false;
  const finish = (hasFinished) => {
    if (isDone) return;
    isDone = true;
    clearTimeout(timer);
    unsubscribe();
    resolve(hasFinished);
  };
  // fetchSOWData restarts an overdue request by setting sowLoading false and, in the same tick,
  // true again. That false is not the end of the load, so the state is looked at again once the
  // synchronous updates have settled before the load is called finished.
  const unsubscribe = useSOWDataStore.subscribe((state) => {
    if (state.sowLoading) return;
    Promise.resolve().then(() => {
      if (!useSOWDataStore.getState().sowLoading) finish(true);
    });
  });
  timer = setTimeout(() => finish(false), SOW_LOAD_WAIT_LIMIT_MS);
});

// The open work order's data, fetching it or waiting for a fetch already running. A refresh in
// progress is waited for even when matching data is cached, as it may add parts. A refresh that
// ends without matching data (it can overwrite the cache with null when connectivity drops) falls
// back to what was cached before, as long as the ticket is still the same. Null when nothing can be
// had (offline with nothing cached, the request failed, or a refresh was still running when the wait
// gave up): the ticket then stays unverified, which holds Submit back and reruns the check on the
// next attempt.
export const waitForMatchingSOWData = async () => {
  const issueKey = queryParamsService.getIssueKey();
  const cached = matchingSOWData();
  const resolve = () => matchingSOWData() ?? (queryParamsService.getIssueKey() === issueKey ? cached : null);

  if (useSOWDataStore.getState().sowLoading) {
    return (await waitForSOWLoadToEnd()) ? resolve() : null;
  }
  if (cached) return cached;

  try {
    await useSOWDataStore.getState().fetchSOWData();
  } catch (error) {
    console.error('Failed to load SOW data for the parts usage check:', error);
    return null;
  }
  const fetched = matchingSOWData();
  if (fetched) return fetched;

  // fetchSOWData returns at once while another request is running; wait for that one.
  if (!useSOWDataStore.getState().sowLoading) return null;
  return (await waitForSOWLoadToEnd()) ? matchingSOWData() : null;
};
